import {
  type AlbumCollectionModelPagedResult,
  type GetApiAlbumCollectionsQueryParams,
  getApiAlbumCollections,
} from '@/shared/api'
import {
  DEBOUNCE_COMMON_TIMEOUT_IN_MS,
  DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
} from '@/shared/config'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@/shared/lib/query'
import { type Query, createQuery } from '@farfetched/core'
import {
  type EventCallable,
  type Store,
  type StoreWritable,
  combine,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { debounce } from 'patronum'

export interface AlbumCollectionsQueryModelBase {
  // Availability
  $isAvailable: StoreWritable<boolean>
  availableSet: EventCallable<void>
  unavailableSet: EventCallable<void>
  //   Filters
  $filters: StoreWritable<GetApiAlbumCollectionsQueryParams>
  filtersInit: EventCallable<Partial<GetApiAlbumCollectionsQueryParams>>
  filtersChanged: EventCallable<Partial<GetApiAlbumCollectionsQueryParams>>
  filtersReset: EventCallable<void>
  //   Search
  $search: StoreWritable<string | null>
  searchChanged: EventCallable<string>
  searchReset: EventCallable<void>
  //   Sort
  $sort: StoreWritable<AlbumCollectionsSortState>
  sortChanged: EventCallable<AlbumCollectionsSortState>
  sortReset: EventCallable<void>
  //   Params
  $params: Store<{
    params: GetApiAlbumCollectionsQueryParams
  }>
  // Query
  query: Query<
    {
      params?: GetApiAlbumCollectionsQueryParams
    },
    AlbumCollectionModelPagedResult,
    unknown
  >
  refreshed: EventCallable<void>
}

export interface AlbumCollectionsQueryModelWithPagination
  extends AlbumCollectionsQueryModelBase {
  $page: StoreWritable<number>
  pageChanged: EventCallable<number>
  pageReset: EventCallable<void>
  $pageSize: StoreWritable<number>
  pageSizeChanged: EventCallable<number>
}

interface AlbumCollectionsQueryModelParams {
  initialFilters?: Partial<GetApiAlbumCollectionsQueryParams>
  usePagination: boolean
}

export interface AlbumCollectionsSortState {
  field: 'name' | 'author' | null
  order: 'asc' | 'desc' | null
}

export function createAlbumCollectionsQueryModel(
  {
    initialFilters = {},
    usePagination = true,
  }: AlbumCollectionsQueryModelParams = {
    initialFilters: {},
    usePagination: true,
  },
): AlbumCollectionsQueryModelWithPagination | AlbumCollectionsQueryModelBase {
  // Query
  const query = createQuery({ handler: getApiAlbumCollections })

  // Availability
  const availableSet = createEvent()
  const unavailableSet = createEvent()
  const $isAvailable = createStore(false)
    .on(availableSet, () => true)
    .on(unavailableSet, () => false)

  // Filters
  const filtersInit = createEvent<Partial<GetApiAlbumCollectionsQueryParams>>()
  const filtersChanged =
    createEvent<Partial<GetApiAlbumCollectionsQueryParams>>()
  const filtersReset = createEvent()
  const $filters = createStore<GetApiAlbumCollectionsQueryParams>(
    initialFilters,
  )
    .on(filtersInit, (_, payload) => ({ ...initialFilters, ...payload }))
    .on(filtersChanged, (state, payload) => ({ ...state, ...payload }))
    .on(filtersReset, () => initialFilters)

  // Search
  const searchChanged = createEvent<string>()
  const searchReset = createEvent()
  const $search = createStore<string | null>(null)

  sample({
    source: query.$status,
    clock: [$filters],
    filter: (status) => status !== 'initial',
    target: [searchReset],
  })

  //   Sort
  const initialSort: AlbumCollectionsSortState = { field: null, order: null }
  const sortChanged = createEvent<AlbumCollectionsSortState>()
  const sortReset = createEvent()

  const $sort = createStore<AlbumCollectionsSortState>(initialSort)
    .on(sortChanged, (_, payload) => payload)
    .on(sortReset, () => initialSort)

  sample({
    source: query.$status,
    clock: [$filters],
    filter: (status) => status !== 'initial',
    target: [sortReset],
  })

  // Pagination

  const $page = createStore<number>(DEFAULT_PAGE)

  const pageChanged = createEvent<number>()
  const pageReset = createEvent()
  $page
    .on(pageChanged, (_, page) => Math.max(DEFAULT_PAGE, page))
    .on(pageReset, () => DEFAULT_PAGE)

  const $pageSize = createStore<number>(DEFAULT_PAGE_SIZE)
  const pageSizeChanged = createEvent<number>()
  $pageSize.on(pageSizeChanged, (_, size) => size)

  sample({
    source: query.$status,
    clock: [$filters, $search, $sort],
    filter: (status) => status !== 'initial',
    target: pageReset,
  })

  // Query params
  const $params = combine(
    $filters,
    $search,
    $page,
    $pageSize,
    $sort,
    (filters, search, page, pageSize, sort) => {
      // Filters
      const params: GetApiAlbumCollectionsQueryParams = { ...filters }
      if (search) params.SearchQuery = search.trim()
      // Sort
      if (sort.field && sort.order) {
        if (sort.field === 'name') {
          if (sort.order === 'asc') params.SortByName = true
          else params.SortByNameDesc = true
        }
        if (sort.field === 'author') {
          if (sort.order === 'asc') params.SortByAuthor = true
          else params.SortByAuthorDesc = true
        }
      }
      // Pagination
      if (usePagination) {
        params.Limit = pageSize
        params.Offset = (page - 1) * pageSize
      }
      return { params }
    },
  )

  // Debounce
  const paramsDebounced = debounce({
    source: $params,
    timeout: DEBOUNCE_COMMON_TIMEOUT_IN_MS,
  })

  const searchChangedDebounced = debounce({
    source: searchChanged,
    timeout: DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
  })

  const searchResetDebounced = debounce({
    source: searchReset,
    timeout: DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
  })

  // Start query
  sample({
    clock: paramsDebounced,
    filter: $isAvailable,
    target: query.start,
  })

  //   Refresh
  const refreshed = createEvent()
  sample({
    source: $params,
    clock: refreshed,
    filter: $isAvailable,
    target: query.start,
  })

  // Change search

  $search
    .on(searchChangedDebounced, (_, search) => search)
    .reset(searchResetDebounced)

  // Reset logic
  sample({
    clock: unavailableSet,
    target: query.reset,
  })

  sample({
    clock: query.reset,
    target: [filtersReset, searchReset, sortReset, pageReset],
  })

  const model: AlbumCollectionsQueryModelBase = {
    // Availability
    $isAvailable,
    availableSet,
    unavailableSet,
    // Filters
    $filters,
    filtersInit,
    filtersChanged,
    filtersReset,
    // Search
    $search,
    searchChanged,
    searchReset,
    // Sort
    $sort,
    sortChanged,
    sortReset,
    // Params
    $params,
    // Query
    query,
    refreshed,
  }

  const modelWithPagination: AlbumCollectionsQueryModelWithPagination = {
    ...model,
    $page,
    pageChanged,
    pageReset,
    $pageSize,
    pageSizeChanged,
  }

  if (usePagination) {
    return modelWithPagination
  }

  return model
}
